<template>
    <div class="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-gray-50">
        <!-- HEADER -->
        <header class="fixed top-0 left-0 right-0 z-20 backdrop-blur-xl bg-white/80 border-b border-white/20 shadow-lg shadow-red-500/5">
            <div class="flex items-center justify-between px-6 py-2.5">
                <div class="flex items-center gap-4">
                    <!-- Burger visible uniquement sur mobile -->
                    <button 
                        class="p-2.5 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg shadow-red-500/30 md:hidden hover:shadow-xl hover:scale-105 transition-all duration-300" 
                        @click="sidebarOpen = true">
                        <i class="fas fa-bars text-lg"></i>
                    </button>

                    <div class="flex items-center gap-3 group">
                        <div class="relative">
                            <div class="absolute inset-0 bg-gradient-to-r from-red-500 to-red-600 rounded-lg blur opacity-50 group-hover:opacity-75 transition-opacity"></div>
                            <img src="https://storage.victoranw.fr/file/bqB44gPXyi" class="relative h-[28px]" alt="">
                        </div>
                        <span class="font-bold text-2xl bg-gradient-to-r from-red-600 to-red-600 bg-clip-text text-transparent">TermoFlow</span>
                    </div>
                </div>
                
                <!-- Mode Toggle -->
                <div class="hidden md:flex rounded-lg bg-gradient-to-r from-gray-200 to-gray-300 p-1 gap-1 shadow-sm">
                    <a href="/app" class="px-4 py-1.5 text-xs font-medium rounded-lg text-gray-700 hover:bg-gray-100 transition-all duration-200">
                        <i class="fas fa-building mr-1"></i> Bureau
                    </a>
                    <a href="/app/tech/today" class="px-4 py-1.5 text-xs font-medium rounded-lg bg-white text-gray-700 hover:bg-gray-100 transition-all duration-200">
                        <i class="fas fa-map-marked-alt mr-1"></i> Terrain
                    </a>
                </div>
                
                <!-- Search -->
                <div class="hidden md:flex items-center relative group">
                    <i class="fas fa-search absolute left-4 text-slate-800/60"></i>
                    <input 
                        type="text" 
                        placeholder="Rechercher..." 
                        class="w-64 pl-11 pr-4 py-2.5 bg-white/60 border border-red-200/50 rounded-xl text-sm focus:outline-none transition-all duration-300 shadow-sm hover:shadow-md">
                </div>
            </div>
        </header>

        <!-- OVERLAY MOBILE -->
        <div 
            v-if="sidebarOpen" 
            class="fixed inset-0 bg-gradient-to-br from-indigo-900/40 to-slate-900/40 backdrop-blur-sm z-30 md:hidden transition-all duration-300" 
            @click="sidebarOpen = false">
        </div>

        <!-- SIDEBAR -->
        <aside 
            class="fixed top-0 left-0 z-40 h-screen w-[280px] transform transition-all duration-300 md:translate-x-0"
            :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'">
            
            <!-- Glassmorphism Background -->
            <div class="absolute inset-0 bg-gradient-to-b from-white/95 to-white/90 backdrop-blur-xl border-r border-white/20 shadow-2xl"></div>
            
            <div class="relative h-full overflow-y-auto">
                <nav class="flex flex-col py-3 px-3 pb-14">

                    <!-- User Profile -->
                    <div class="mx-2 mb-2 p-3 rounded-xl group transition-all duration-300">
                        <div class="flex items-center justify-center gap-2">
                            <img src="https://storage.victoranw.fr/file/hGf4I3ecnS" class="h-[26px]" alt="TermoFlow Logo">
                            <span class="font-black text-2xl text-black">TermoFlow</span>
                        </div>
                        <div class="mt-0.5 text-xs text-gray-500 italic text-center">
                            La précision au cœur de chaque intervention
                        </div>
                    </div>

                    <!-- <button @click="$modal.show('modal-add-intervention-template')" class="mx-2 bg-slate-900 hover:bg-slate-800 duration-200 text-white px-3 py-3 rounded-lg shadow font-semibold text-sm mb-3">
                        <i class="fas fa-plus"></i> Nouvelle intervention
                    </button> -->

                    <!-- Navigation Links -->
                    <a 
                        href="/app/tech/today" 
                        class="group relative mx-2 mb-0.5 px-3 py-2.5 rounded-lg transition-all duration-200 hover:scale-[1.01]" 
                        :class="currentRouteKey == 'today' ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-md font-semibold' : 'font-medium text-gray-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-slate-50'">
                        <div class="flex items-center gap-2.5">
                            <i class="fas fa-rocket text-base" :class="currentRouteKey == 'today' ? 'text-white' : 'text-slate-700'"></i>
                            <span class="text-sm">Mission du jour</span>
                        </div>
                    </a>
                    <a 
                        href="/app/tech/today" 
                        class="group relative mx-2 mb-0.5 px-3 py-2.5 rounded-lg transition-all duration-200 hover:scale-[1.01]" 
                        :class="currentRouteKey == 'home' ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-md font-semibold' : 'font-medium text-gray-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-slate-50'">
                        <div class="flex items-center gap-2.5">
                            <i class="fas fa-calendar text-base" :class="currentRouteKey == 'home' ? 'text-white' : 'text-slate-700'"></i>
                            <span class="text-sm">Planning</span>
                        </div>
                    </a>
                    <a 
                        href="/app/tech/account" 
                        class="group relative mx-2 mb-0.5 px-3 py-2.5 rounded-lg transition-all duration-200 hover:scale-[1.01]" 
                        :class="currentRouteKey == 'account' ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-md font-semibold' : 'font-medium text-gray-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-slate-50'">
                        <div class="flex items-center gap-2.5">
                            <i class="fas fa-user text-base" :class="currentRouteKey == 'account' ? 'text-white' : 'text-slate-700'"></i>
                            <span class="text-sm">Profil</span>
                        </div>
                    </a>
                </nav>

                <!-- Footer -->
                <div class="fixed bottom-0 left-0 w-[280px] pr-2 p-4 bg-gradient-to-t from-white/95 to-transparent backdrop-blur-sm border-t border-white/20">
                    <div class="text-center text-xs text-gray-500 font-medium">
                        © 2025 TermoFlow
                    </div>
                </div>
            </div>
        </aside>

        <!-- CONTENT -->
        <main class="mt-[64px] md:pl-[300px] p-6 md:p-8 min-h-screen max-w-[1000px] mx-auto">
            <slot></slot>
        </main>

        <modal-add-intervention id="modal-add-intervention-template" />
    </div>
</template>

<script>
import ModalAddIntervention from '@/views/Intervention/Components/ModalAddIntervention.vue';
export default {
    name: "AppTemplate",
    components: {
        ModalAddIntervention
    },
    data() {
        return {
            sidebarOpen: false,
            configurationsOpen: false,
        }
    },
    computed: {
        currentRouteKey() {
            let routeName = this.$route.name;
            let keyGroup = {
                TechToday: 'today',
                TechAccountView: 'account',
            };
            return keyGroup[routeName] || '';
        }
    }
}
</script>

<style scoped>
/* Smooth scrollbar */
aside::-webkit-scrollbar {
    width: 6px;
}

aside::-webkit-scrollbar-track {
    background: transparent;
}

aside::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, #6366f1, #a855f7);
    border-radius: 10px;
}

aside::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(to bottom, #4f46e5, #9333ea);
}
</style>
